import { useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const SfcRegionModelDialog = ({
  openModal,
  setOpenModal,
  sfcRegions = [],
  setSfcRegionFormData = () => {},
} = {}) => {
  const [formData, setFormData] = useState({
    businessEntityID: "",
    regionName: "",
    localCurrency: "",
    country: "",
  });

  const [touched, setTouched] = useState({
    businessEntityID: false,
    regionName: false,
    localCurrency: false,
    country: false,
  });

  const handleBlur = (field) => {
    setTouched((prevTouched) => ({ ...prevTouched, [field]: true }));
  };

  const isDuplicateSfc = (businessEntityIDToCheck) => {
    return sfcRegions.some((region) =>
      region?.sfcs?.some(
        (sfc) => sfc?.businessEntityID === businessEntityIDToCheck,
      ),
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "localCurrency") {
      const upperCaseValue = value.toUpperCase();

      const isValid = /^[A-Z]{0,3}$/.test(upperCaseValue);

      if (isValid) {
        newValue = upperCaseValue;
      } else {
        return;
      }
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));
  };

  const resetForm = () => {
    setFormData({
      businessEntityID: "",
      regionName: "",
      localCurrency: "",
      country: "",
    });
    setTouched({
      businessEntityID: false,
      regionName: false,
      localCurrency: false,
      country: false,
    });
  };

  const handleCloseModal = (event, reason) => {
    if (reason && reason === "backdropClick") {
      return; // Prevent closing when clicked outside
    }
    resetForm();
    setOpenModal(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const finalFormData = {
      ...formData,
    };

    setSfcRegionFormData(finalFormData || {});

    setOpenModal(false);
    resetForm();
  };

  return (
    <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Region / SFC Permissions
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ mt: 0.5 }} />
      <DialogContent>
        <form onSubmit={handleFormSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Region Name"
                fullWidth
                required
                name="regionName"
                value={formData.regionName}
                onChange={handleInputChange}
                onBlur={() => handleBlur("regionName")}
                margin="normal"
                autoComplete="off"
                error={touched.regionName && !formData.regionName}
                helperText={
                  touched.regionName &&
                  !formData.regionName &&
                  "Region name is required."
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Business Entity"
                fullWidth
                required
                name="businessEntityID"
                value={formData.businessEntityID}
                onChange={handleInputChange}
                onBlur={() => handleBlur("businessEntityID")}
                margin="normal"
                autoComplete="off"
                error={
                  (touched.businessEntityID && !formData.businessEntityID) ||
                  isDuplicateSfc(formData.businessEntityID)
                }
                helperText={
                  touched.businessEntityID && !formData.businessEntityID
                    ? "Business Entity ID is required."
                    : isDuplicateSfc(formData.businessEntityID)
                      ? "Business Entity ID already exists!"
                      : ""
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Local Currency (Max 3 char.)"
                fullWidth
                required
                name="localCurrency"
                value={formData.localCurrency}
                onChange={handleInputChange}
                onBlur={() => handleBlur("localCurrency")}
                margin="normal"
                autoComplete="off"
                error={touched.localCurrency && !formData.localCurrency}
                helperText={
                  touched.localCurrency && !formData.localCurrency
                    ? "Local Currency is required with max 3 char."
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Country"
                fullWidth
                required
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                onBlur={() => handleBlur("country")}
                margin="normal"
                autoComplete="off"
                error={touched.country && !formData.country}
                helperText={
                  touched.country && !formData.country
                    ? "Country is required."
                    : ""
                }
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={handleCloseModal} color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={
                !formData.regionName ||
                !formData.businessEntityID ||
                !formData.localCurrency ||
                !formData.country ||
                isDuplicateSfc(formData.businessEntityID)
              }
            >
              Create Region / SFC
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SfcRegionModelDialog;
